import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, streamChat, pollJob, errMsg } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { FloorPlan2D } from "@/components/FloorPlan2D";
import { Massing3D } from "@/components/Massing3D";
import { RoomSchedule } from "@/components/RoomSchedule";
import { ChatPanel } from "@/components/ChatPanel";
import { ExportPanel } from "@/components/ExportPanel";
import { RhinoLive } from "@/components/RhinoLive";
import { SitePlan } from "@/components/SitePlan";
import { SitePanel } from "@/components/SitePanel";
import { LayerToggles } from "@/components/Fitout2D";
import { FitoutSummary } from "@/components/FitoutSummary";
import { PayCard, LockedExports } from "@/components/BillingWidgets";
import { SharePanel } from "@/components/SharePanel";
import { useBilling, money } from "@/components/Billing";
import { estimateRevision, revisionCost } from "@/lib/estimate";
import { fmtFt, floorView, wallMoveRange, applyWallMove } from "@/lib/walls";

const Tabs = ({ tab, setTab }) => (
  <div className="flex border border-[var(--line)] font-mono text-[11px] uppercase tracking-widest">
    {[["2d", "2D Plan"], ["3d", "3D"], ["site", "Site"]].map(([k, l]) => (
      <button key={k} data-testid={`canvas-tab-${k}`} onClick={() => setTab(k)}
        className={`px-4 py-2 transition-colors ${tab === k ? "bg-[var(--cyan)] text-[var(--bg)]" : "text-[var(--muted)] hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

const Stat = ({ k, v }) => (
  <div><div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">{k}</div><div className="font-mono text-sm mt-1">{v}</div></div>
);

const EmptyCanvas = ({ streaming }) => (
  <div className="relative h-full grid place-items-center overflow-hidden scanline" data-testid="canvas-empty">
    <div className="text-center">
      {streaming ? <Loader2 className="mx-auto animate-spin text-[var(--cyan)]" /> : null}
      <p className="mt-4 font-mono text-xs uppercase tracking-widest text-[var(--muted)]">{streaming ? "AI is drafting your plan…" : "No draft yet"}</p>
    </div>
  </div>
);

export default function Workspace() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("2d");
  const [sel, setSel] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [floor, setFloor] = useState(0);
  const [drawKey, setDrawKey] = useState(0);
  const [layers, setLayers] = useState({ furniture: true, electrical: false, plumbing: false });
  const [memCount, setMemCount] = useState(0);
  const { billing } = useBilling();
  const estimateHint = (text) => {
    if (!project?.plan) return null;
    const c = revisionCost(estimateRevision(text, project.plan), project.licensed_sqft ?? project.plan.total_area);
    if (!c) return null;
    if (billing?.is_admin) return <span className="text-[var(--emerald)]">Est. {c.total.toLocaleString()} sq ft ({c.basis}) · admin, no charge</span>;
    return c.extra
      ? <span className="text-[var(--amber)]">Est. {c.total.toLocaleString()} sq ft ({c.basis}) → ~{c.extra.toLocaleString()} sq ft over license · ~{money(c.cost)} if the AI's plan comes out that big</span>
      : <span className="text-[var(--emerald)]">Est. {c.total.toLocaleString()} sq ft ({c.basis}) · within your {Math.round(project.licensed_sqft ?? 0).toLocaleString()} sq ft license — free</span>;
  };
  useEffect(() => { api.get("/memory").then(({ data }) => setMemCount(data.enabled ? data.prefs.length + data.habits.length : 0)).catch(() => {}); }, [project?.version]);
  const auto = useRef(false);
  const dragBase = useRef(null);

  const onWallDrag = (seg, d, done) => {
    if (!dragBase.current) dragBase.current = project.plan;
    const base = dragBase.current;
    const [lo, hi] = wallMoveRange(floorView(base, floor), seg);
    const dd = Math.min(hi, Math.max(lo, d));
    const next = applyWallMove(base, floor, seg, dd);
    setProject((p) => ({ ...p, plan: next }));
    if (!done) return;
    dragBase.current = null;
    if (dd === 0) return;
    api.put(`/projects/${id}/plan`, { plan: next })
      .then(({ data }) => { setProject((p) => ({ ...p, plan: data.plan, version: data.version })); toast.success("Wall moved · saved"); })
      .catch((e) => toast.error(errMsg(e)));
  };

  const reload = useCallback(() => api.get(`/projects/${id}`).then(({ data }) => setProject(data)), [id]);

  const onEv = useCallback((ev) => {
    if (ev.type === "text") setStreamText(ev.text);
    if (ev.type === "needs_payment") { toast.message("This revision needs a larger license"); reload(); }
    if (ev.type === "plan") {
      setProject((p) => ({ ...p, plan: ev.plan, title: ev.title, version: ev.version, messages: [...p.messages, { role: "assistant", content: ev.message }] }));
      setFloor((f) => Math.min(f, ev.plan.floors.length - 1));
      setDrawKey((k) => k + 1);
      toast.success("Draft updated");
    }
    if (ev.type === "error") {
      toast.error(ev.message);
      setProject((p) => ({ ...p, messages: [...p.messages, { role: "assistant", content: ev.message }] }));
    }
  }, [reload]);

  const follow = useCallback(async (fn) => {
    setStreaming(true); setStreamText("");
    try { await fn(); } catch (e) { toast.error(errMsg(e)); }
    setStreaming(false); setStreamText("");
  }, []);

  const send = useCallback((message) => {
    setProject((p) => ({ ...p, messages: [...p.messages, { role: "user", content: message }] }));
    return follow(() => streamChat(id, message, onEv));
  }, [id, onEv, follow]);

  useEffect(() => {
    Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/job`)]).then(([{ data }, { data: job }]) => {
      if (auto.current) return;
      auto.current = true;
      if (job.status === "running") {
        setProject({ ...data, messages: [...data.messages, { role: "user", content: job.message }] });
        follow(() => pollJob(id, onEv));
        return;
      }
      setProject(data);
      if (!data.plan && data.messages.length === 0 && !data.needs_payment && !data.pending_plan) send(data.prompt);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id, send, follow, onEv]);

  if (!project) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[var(--cyan)]" /></div>;
  const plan = project.plan;
  const nFl = plan?.floors.length || 0;
  const fv = plan ? floorView(plan, Math.min(floor, nFl - 1)) : null;
  const stairDir = floor < nFl - 1 ? "UP" : floor > 0 ? "DN" : null;

  return (
    <div className="h-screen flex flex-col" data-testid="workspace-page">
      <AppHeader>
        <span className="h-5 w-px bg-[var(--line)]" />
        <Link to="/dashboard" className="text-[var(--muted)] hover:text-white transition-colors" data-testid="back-to-dashboard"><ChevronLeft size={18} /></Link>
        <h1 className="text-sm font-semibold truncate" data-testid="project-title">{project.title}</h1>
        <span className="hidden sm:inline font-mono text-[10px] px-2 py-0.5 border border-[var(--line)] text-[var(--muted)]">v{project.version}</span>
      </AppHeader>
      <div className="flex-1 min-h-0 grid lg:grid-cols-[360px_1fr_320px] overflow-y-auto lg:overflow-hidden">
        <aside className="border-r border-[var(--line)] bg-[var(--surface)] min-h-[480px] lg:min-h-0 flex flex-col order-2 lg:order-1">
          <div className="px-5 py-3 border-b border-[var(--line)] font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex justify-between gap-2">
            <span>Design assistant · Claude Opus</span>
            {memCount > 0 && <Link to="/dashboard#dna" className="text-[var(--cyan)] hover:underline" data-testid="memory-chip">Knows {memCount} of your prefs</Link>}
          </div>
          {(project.needs_payment || project.pending_plan) && !streaming && (
            <PayCard project={project} onPaid={reload} onDiscard={() => api.post(`/projects/${id}/discard-pending`).then(reload).catch((e) => toast.error(errMsg(e)))} />
          )}
          <div className="flex-1 min-h-0">
            <ChatPanel messages={project.messages} streaming={streaming} streamText={streamText} onSend={send} hasPlan={!!plan} locked={!!(project.needs_payment || project.pending_plan || project.locked)} estimate={estimateHint} />
          </div>
        </aside>
        <main className="relative min-h-[420px] lg:min-h-0 flex flex-col order-1 lg:order-2 blueprint-grid">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[var(--line)] glass">
            <Tabs tab={tab} setTab={setTab} />
            {plan && tab === "2d" && nFl > 1 && (
              <div className="flex border border-[var(--line)] font-mono text-[10px] uppercase tracking-widest" data-testid="floor-selector">
                {plan.floors.map((f, i) => (
                  <button key={i} data-testid={`floor-tab-${i}`} onClick={() => setFloor(i)}
                    className={`px-3 py-2 transition-colors ${floor === i ? "bg-[var(--amber)] text-[var(--bg)]" : "text-[var(--muted)] hover:text-white"}`}>L{i + 1} · {f.name}</button>
                ))}
              </div>
            )}
            {plan && tab === "2d" && !project.locked && <span className="hidden xl:block font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] truncate">Drag interior walls to resize rooms</span>}
          </div>
          <div className="flex-1 min-h-0 relative">
            {!plan ? <EmptyCanvas streaming={streaming} /> : tab === "2d" ? (
              <div className="absolute inset-0 p-4 md:p-8 pt-14 md:pt-14">
                <div className="absolute top-3 left-4 z-10"><LayerToggles layers={layers} setLayers={setLayers} /></div>
                <FloorPlan2D key={`${drawKey}-${floor}`} plan={fv} selectedId={sel} onSelect={setSel} animate hideDims={!!project.locked} onWallDrag={streaming || project.locked ? undefined : onWallDrag} stairDir={stairDir} layers={layers} />
              </div>
            ) : tab === "3d" ? <div className="absolute inset-0"><Massing3D plan={plan} /></div>
              : <div className="absolute inset-0 p-4 md:p-8"><SitePlan plan={plan} site={project.site} hideDims={!!project.locked} /></div>}
            {plan && streaming && <div className="absolute inset-0 bg-[var(--bg)]/50 scanline pointer-events-none" />}
          </div>
        </main>
        <aside className="border-l border-[var(--line)] bg-[var(--surface)] overflow-y-auto scroll-thin order-3 p-5 space-y-8">
          {plan ? (
            <>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--cyan)] mb-4">Rhino</p>
                <div className="space-y-3">
                  {project.locked ? <LockedExports project={project} onPaid={reload} /> : <>
                    <RhinoLive project={project} />
                    <ExportPanel project={project} />
                  </>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 border-y border-[var(--line)] py-4" data-testid="plan-stats">
                <Stat k="Area" v={`${Math.round(plan.total_area)} SF`} />
                <Stat k="Footprint" v={project.locked ? "Locked" : `${fmtFt(plan.width)}×${fmtFt(plan.depth)}`} />
                <Stat k="Levels" v={nFl} />
              </div>
              <SharePanel project={project} onChange={reload} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--cyan)] mb-3">Room schedule</p>
                <RoomSchedule plan={plan} selectedId={sel} onSelect={setSel} hideDims={!!project.locked} />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--cyan)] mb-3">Fit-out · MEP</p>
                <FitoutSummary plan={plan} />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--cyan)] mb-3">Lot & setbacks</p>
                <SitePanel project={project} streaming={streaming} onFit={send}
                  onSaved={(site) => setProject((p) => ({ ...p, site, version: p.version + 1 }))} />
              </div>
              {plan.notes && <p className="text-xs text-[var(--muted)] leading-relaxed border-l-2 border-[var(--amber)] pl-3" data-testid="plan-notes">{plan.notes}</p>}
            </>
          ) : <p className="text-sm text-[var(--muted)]">Exports and the room schedule appear once the first draft is ready.</p>}
        </aside>
      </div>
    </div>
  );
}
